<template>
  <div>
    <v-dialog v-model="showDialog" max-width="650">
      <BaseTaskForm
        v-if="showDialog"
        :associated-records="associatedRecords"
        :collection-type="collectionType"
        :save-loading="saveLoading"
        :task-entry="taskEntry"
        title="Edit Task"
        @close="closeHandler"
        @submit="editHandler"
        @archive="archiveHandler"
      />
    </v-dialog>
    <ConfirmationDialog
      v-model="showConfirmationDialog"
      @cancel="showConfirmationDialog = false"
      @submit="archiveSubmitHandler"
    >
      Are you sure you want to archive this task?
    </ConfirmationDialog>
    <ConfirmationDialog
      v-model="showUpdateAllRecurringEvents"
      @cancel="showUpdateAllRecurringEvents = false"
      @submit="submitRecurringHandler"
    >
      You changed the schedule of a recurring event. This will update the schedule for all events in the series. Do you want to proceed?
    </ConfirmationDialog>
    <ConfirmationDialog
      v-model="showRecurringConfirmationDialog"
      :max-width="350"
      :title="isDeleteRecurringEvents ? 'Archive recurring event' : 'Edit recurring event'"
      @cancel="showRecurringConfirmationDialog = false"
      @submit="submitRecurringHandler"
    >
      <div>
        <v-radio-group v-model="isEditAllRecurringEvents">
          <v-radio label="This event" :value="false" />
          <v-radio label="All events" :value="true" />
        </v-radio-group>
      </div>
    </ConfirmationDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { DialogMixin } from '@/bundles/Dialogs/mixins/DialogMixin';
import { taskService } from '@/bundles/Tasks/factories/taskServiceFactory';
import { ITask } from '@/bundles/Tasks/interfaces/ITask';
import { Nullable } from '@/utils/types';
import { ITaskCreateUpdatePayload } from '@/bundles/Tasks/interfaces/ITaskCreateUpdate';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import BaseTaskForm from '@/bundles/Tasks/components/BaseTaskForm.vue';
import ConfirmationDialog from '@/bundles/Common/components/ConfirmationDialog.vue';

interface IEditTaskForm {
  saveLoading: boolean;
  showConfirmationDialog: boolean;
  showRecurringConfirmationDialog: boolean;
  isEditAllRecurringEvents: boolean;
  isDeleteRecurringEvents: boolean;
  showUpdateAllRecurringEvents: boolean;
  taskEditPayload: Nullable<ITaskCreateUpdatePayload>;
}

export default defineComponent({
  name: 'EditTaskForm',

  components: { BaseTaskForm, ConfirmationDialog },
  mixins: [DialogMixin],

  props: {
    collectionType: {
      type: String,
      default: null
    },
    associatedRecords: {
      type: Array as PropType<any[]>, // todo: type this
      default: () => []
    },
    taskEntry: {
      type: Object as PropType<Nullable<ITask>>,
      default: null
    },
  },

  emits: ['fetch:tasks', 'submit', 'close'],

  setup () {
    const { addNotification, addSuccessNotification } = useNotifications();

    return { addNotification, addSuccessNotification, LocalizationEnum };
  },

  data: (): IEditTaskForm => ({
    saveLoading: false,
    showConfirmationDialog: false,
    showRecurringConfirmationDialog: false,
    isEditAllRecurringEvents: false,
    isDeleteRecurringEvents: false,
    showUpdateAllRecurringEvents: false,
    taskEditPayload: null,
  }),

  watch: {
    showDialog (val: boolean) {
      if (!val) {
        this.isEditAllRecurringEvents = false;
        this.isDeleteRecurringEvents = false;
        this.showUpdateAllRecurringEvents = false;
        this.taskEditPayload = null;
      }
    }
  },

  methods: {
    editHandler (task: ITaskCreateUpdatePayload) {
      if (!this.taskEntry) return;

      this.taskEditPayload = task;

      if (!this.taskEntry.recurrent_group_id) {
        this.submitHandler();
        return;
      }

      if (this.taskEntry.schedule !== this.taskEditPayload.schedule) {
        this.showUpdateAllRecurringEvents = true;
        return;
      }

      this.isDeleteRecurringEvents = false;
      this.showRecurringConfirmationDialog = true;
    },
    async submitHandler () {
      try {
        if (!this.taskEntry || !this.taskEditPayload) {
          return;
        }

        this.saveLoading = true;

        const payload = {
          task: this.taskEditPayload,
          id: (this.taskEntry as ITask)._key
        };

        if (this.taskEntry.recurrent_group_id) {
          Object.assign(payload, { edit_all: this.isEditAllRecurringEvents });
        }

        const newTask = await taskService.updateTask(payload);

        this.addSuccessNotification('Task updated successfully.');

        if ((this.taskEntry.assignee !== this.taskEditPayload.assignee) || (this.taskEntry.recurrent_group_id && this.isEditAllRecurringEvents)) { // need to fetch tasks in different sections
          this.$eventBus.$emit('fetch:tasks');
        } else {
          this.$emit('submit', newTask);
        }
        this.showDialog = false;
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.saveLoading = false;
        this.taskEditPayload = null;
      }
    },
    async archiveSubmitHandler () {
      try {
        if (!this.taskEntry) {
          return;
        }
        this.showConfirmationDialog = false;
        this.saveLoading = true;

        const payload = { keys: [(this.taskEntry as ITask)._key] };

        if (this.taskEntry.recurrent_group_id) {
          Object.assign(payload, { edit_all: this.isEditAllRecurringEvents });
        }

        await taskService.archive(payload);

        const tasksText = this.$tc(this.LocalizationEnum.task);
        const wasText = this.$tc(this.LocalizationEnum.was);

        this.$emit('fetch:tasks');
        this.showDialog = false;
        this.addNotification({
          title: 'Success',
          status: 200,
          message: `${tasksText} '${this.taskEntry.title}' ${wasText} archived.`,
        });
      } catch (error: any) {
        this.addNotification(error);
      } finally {
        this.saveLoading = false;
      }
    },
    closeHandler () {
      this.showDialog = false;
      this.$emit('close');
    },
    archiveHandler () {
      if (!this.taskEntry) return;

      this.isDeleteRecurringEvents = true;
      this.taskEntry.recurrent_group_id ? this.showRecurringConfirmationDialog = true : this.showConfirmationDialog = true;
    },
    submitRecurringHandler () {
      this.showUpdateAllRecurringEvents = false;
      this.showRecurringConfirmationDialog = false;
      this.isDeleteRecurringEvents ? this.archiveSubmitHandler() : this.submitHandler();
    }
  }
});
</script>
